import { differenceInDays } from 'date-fns'
import {
  Archive,
  ArrowRight,
  Bot,
  Combine,
  Database,
  Layers,
  Zap,
} from 'lucide-react'
import { use, useMemo } from 'react'
import { Link } from 'react-router'
import { CompanySuspensionContext } from '@/components/company-suspension/context'
import { useOnboardingSheet } from '@/components/onboarding-sheet/context'
import { CreditAndExpiryDisplay } from '@/components/shared/credits-and-expiry/credit-and-expiry-display'
import { H1, H2, Lead, Small } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { gql } from '@/lib/graphql/gql'
import {
  useCompanyBilling,
  useCompanyPath,
  useIdCompany,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import { cn } from '@/lib/utils'
import * as Styled from '@/routes/home/styles'
import type {
  EmptyStateQuery,
  EmptyStateQueryVariables,
} from '@/routes/home/empty-state/empty-page.types.gen'

const EMPTY_STATE_QUERY = gql`
  query EmptyState($idCompany: String!) {
    user {
      id
      firstName
    }
    company(id: $idCompany) {
      id
      sevallaBilling {
        id
        creditUsage {
          id
          remainingCredits
          expiresAt
        }
      }
    }
  }
`

export const EmptyPage = () => {
  const { open: openOnboarding, isDismissed: isOnboardingDismissed } =
    useOnboardingSheet()
  const { rbac } = useRBAC()
  const { idCompany } = useIdCompany()
  const { getCompanyPath } = useCompanyPath()
  const { isSuspended } = use(CompanySuspensionContext)
  const { hasValidBankcard } = useCompanyBilling()
  const { data } = useSuspenseQuery<EmptyStateQuery, EmptyStateQueryVariables>(
    EMPTY_STATE_QUERY,
    {
      variables: { idCompany },
      fetchPolicy: 'cache-and-network',
    }
  )

  const firstName = useMemo(() => {
    return data?.user?.firstName || ''
  }, [data])

  const company = useMemo(() => data?.company, [data])
  const balance = useMemo(() => {
    const stripeBalance =
      company?.sevallaBilling?.creditUsage?.remainingCredits ?? 0
    return Math.floor(stripeBalance / 100)
  }, [company])
  const daysUntilCreditExpiry = useMemo(
    () =>
      company?.sevallaBilling?.creditUsage?.expiresAt
        ? differenceInDays(
            new Date(company.sevallaBilling.creditUsage.expiresAt).getTime(),
            new Date().getTime()
          )
        : null,
    [company]
  )
  const expiresAt = useMemo(() => {
    if (daysUntilCreditExpiry === null) {
      return null
    }
    return daysUntilCreditExpiry === 1
      ? '1 day'
      : `${daysUntilCreditExpiry} days`
  }, [daysUntilCreditExpiry])

  const hasCredits = useMemo(() => balance > 0, [balance])

  const hasCreditExpiry = useMemo(
    () => daysUntilCreditExpiry !== null,
    [daysUntilCreditExpiry]
  )

  const hasCardOrCredits = useMemo(
    () => hasValidBankcard || hasCredits,
    [hasCredits, hasValidBankcard]
  )

  const deployServiceButtons = useMemo(
    () => [
      {
        icon: Layers,
        label: 'Deploy an application',
        disabled:
          !hasValidBankcard ||
          isSuspended ||
          !rbac.company(idCompany).apps.create.can(),
        to: getCompanyPath({ pathname: '/apps/create' }),
      },
      {
        icon: Combine,
        label: 'Create an application pipeline',
        disabled:
          !hasValidBankcard ||
          isSuspended ||
          !rbac.company(idCompany).apps.create.can(),
        to: getCompanyPath({ pathname: '/apps/create-pipeline' }),
      },
      {
        icon: Database,
        label: 'Deploy a database',
        disabled:
          !hasCardOrCredits ||
          isSuspended ||
          !rbac.company(idCompany).databases.create.can(),
        to: getCompanyPath({ pathname: '/databases/create' }),
      },
      {
        icon: Zap,
        label: 'Deploy a static site',
        disabled:
          !hasValidBankcard ||
          isSuspended ||
          !rbac.company(idCompany).staticSites.create.can(),
        to: getCompanyPath({ pathname: '/static-sites/create' }),
      },
      {
        icon: Archive,
        label: 'Create object storage',
        disabled:
          !hasValidBankcard ||
          isSuspended ||
          !rbac.company(idCompany).objectStorages.create.can(),
        to: getCompanyPath({ pathname: '/object-storage/create' }),
      },
    ],
    [
      hasCardOrCredits,
      isSuspended,
      rbac,
      idCompany,
      getCompanyPath,
      hasValidBankcard,
    ]
  )

  return (
    <Card
      className={cn(
        'bg-conic-115',
        'from-[#FF7600] dark:from-orange-700',
        'via-[#FF9146] dark:via-orange-600',
        'to-[#FFBB8D] dark:to-orange-500'
      )}
    >
      <CardContent className="px-8 pt-6 pb-8">
        <Styled.EmptyContainer>
          <div>
            <H1 className="text-4xl text-start text-white">{`Hello${firstName ? `, ${firstName}` : ''}`}</H1>
            <Lead className="text-white/80 text-lg mt-1">
              Let's start shipping!
            </Lead>
          </div>
          {!isOnboardingDismissed && (
            <button
              onClick={() => openOnboarding('agent-select')}
              className={cn(
                'group relative overflow-hidden rounded-xl p-5 text-left w-full',
                'bg-white/10 backdrop-blur-sm border-2 border-white/50',
                'hover:bg-white/20 hover:border-white/70 hover:shadow-lg',
                'transition-all duration-200 active:scale-[0.99]'
              )}
            >
              <div className="flex items-center gap-4">
                <div className="shrink-0 flex items-center justify-center size-12 rounded-xl bg-white/20 border border-white/40">
                  <Bot className="size-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <H2 className="text-white text-base">
                      Connect your AI agent
                    </H2>
                    <span className="inline-flex items-center rounded-full bg-white/20 border border-white/30 px-2 py-0.5 text-xs text-white font-medium">
                      Recommended
                    </span>
                  </div>
                  <p className="text-white/80 text-sm">
                    Use Claude Code, Cursor, Windsurf, OpenCode, or Codex to
                    manage your Sevalla services — without leaving your coding
                    environment.
                  </p>
                </div>
                <ArrowRight className="shrink-0 size-5 text-white/70 transition-transform group-hover:translate-x-1" />
              </div>
            </button>
          )}
          <Styled.GlassCardContainer>
            <Styled.GlassCard className="row-span-2 flex flex-col gap-4 p-6">
              <H2 className="text-white text-md">Deploy your own service</H2>
              <div className="grid gap-6">
                {deployServiceButtons.map((button) => (
                  <Button
                    key={button.label}
                    variant="ghost"
                    disabled={button.disabled}
                    className="group justify-start text-white border border-white/30 px-4"
                    size="lg"
                    asChild={!button.disabled}
                  >
                    <Link
                      to={button.to}
                      className="w-full flex items-center gap-2 px-0"
                    >
                      <button.icon className="size-4" /> {button.label}{' '}
                      <ArrowRight className="size-4 ml-auto transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                ))}
              </div>
            </Styled.GlassCard>
            {hasCredits ? (
              <Styled.GlassCard className="p-6">
                <CreditAndExpiryDisplay
                  variant="glass"
                  balance={balance}
                  expiresAt={expiresAt}
                  hasCreditExpiry={hasCreditExpiry}
                />
              </Styled.GlassCard>
            ) : (
              <div className="hidden xl:block" />
            )}
            <Styled.GlassCard className="p-6">
              <H2 className="text-white text-md">Get help from us</H2>
              <Small className="block leading-7 text-white/90 mt-2">
                Have you got any questions or need assistance with your
                framework or our services?
                <br />
                Open the chat, we'll be happy to help.
              </Small>
            </Styled.GlassCard>
          </Styled.GlassCardContainer>
        </Styled.EmptyContainer>
      </CardContent>
    </Card>
  )
}
